import React, { useState, useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"
import {
  Alert,
  Button,
  Col,
  Container,
  Table,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap"
import { useQuery } from "@tanstack/react-query"
import Select from "react-select"

import storage from "services/storage"
import { authUserKey } from "config/API"
import { getTickets } from "services/tickets"
import { Loading } from "components"
import { TicketItemList } from "components/sales"
import { getUsers } from "services/users"
import TablePaginationFooter from "components/TablePaginationFooter"
import TicketRetrive from "./ticketRetrive"
import ListFilterBar from "ui-componets/list-filter-bar"
import { getDateFilterParams } from "utils/date-filters"
import "assets/scss/custom/components/table-interactive.scss"

const PAGE_SIZE = 30

const getSingleFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.length === 1 ? value[0] : "all"
  }

  return value
}

const getPaymentStatusFilterParams = (value) => {
  if (value === "pagare") {
    return { is_receivable: "true" }
  }

  if (value === "true" || value === "false") {
    return { is_paid: value, is_receivable: "false" }
  }

  return {}
}

const paymentMethodOptions = [
  { value: "all", label: "Todos" },
  { value: "01 - Efectivo.", label: "Efectivo" },
  { value: "28 - Tarjeta de débito.", label: "Tarjeta" },
  { value: "03 - Transferencia electrónica de fondos.", label: "Transferencia" },
  { value: "PAGARE - Pagare.", label: "Pagare" },
  { value: "99 - Por definir.", label: "Otros" },
]

const getPaymentMethodFilterParams = (value) => {
  const values = Array.isArray(value) ? value : value && value !== "all" ? [value] : []

  return values.length ? { payment_method: values.join(",") } : {}
}

const TicketByUser = () => {
  document.title = "Ventas realizadas | Punto de venta"

  const Location = useLocation()
  const [filters, setFilters] = useState("")
  const [searchTxt, setSearchTxt] = useState(null)
  const [appliedUser, setAppliedUser] = useState(null)
  const [dateFilter, setDateFilter] = useState({ preset: "", startDate: "", endDate: "" })
  const [paymentStatus, setPaymentStatus] = useState("all")
  const [paymentMethod, setPaymentMethod] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [modalTicketOpen, setModalTicketOpen] = useState(false)
  const [ticketId, setTicketId] = useState(null)

  const queryFilters = useMemo(
    () => {
      const paymentStatusValue = getSingleFilterValue(paymentStatus)

      return {
        offset: (currentPage - 1) * PAGE_SIZE,
        per_page: PAGE_SIZE,
        ordering: "-created_at",
        ...getDateFilterParams(dateFilter, "date"),
        ...getPaymentStatusFilterParams(paymentStatusValue),
        ...getPaymentMethodFilterParams(paymentMethod),
        ...(appliedUser
          ? {
              user__email: appliedUser.email,
              user__first_name: appliedUser.first_name,
              user__last_name: appliedUser.last_name,
            }
          : {}),
      }
    },
    [currentPage, appliedUser, dateFilter, paymentMethod, paymentStatus]
  )

  const tickets = useQuery(["tickets-by-user", queryFilters], () => getTickets(queryFilters), {
    keepPreviousData: true,
  })
  const users = useQuery(["users", filters, filters.toString()], () => getUsers(filters))
  const usersFiltered = users?.data?.results.filter((user) => user.user_type === "A" || user.user_type === "S")
  const styleCenterTable = { verticalAlign: "middle" }

  const isLoading = tickets.isLoading
  const permissions = storage.get(authUserKey).user_permissions || []

  useEffect(() => {
    setFilters(Location.search)
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [appliedUser])

  function handleChangeSearch(event) {
    setSearchTxt(event)
  }

  const handleSearch = () => {
    setAppliedUser(searchTxt)
    setCurrentPage(1)
  }

  const handleDateFilterChange = (nextDateFilter) => {
    setCurrentPage(1)
    setDateFilter(nextDateFilter)
  }

  const handlePaymentStatusChange = (value) => {
    setCurrentPage(1)
    setPaymentStatus(value)
  }

  const handlePaymentMethodChange = (value) => {
    setCurrentPage(1)
    setPaymentMethod(value)
  }

  const clearListFilters = () => {
    setCurrentPage(1)
    setDateFilter({ preset: "", startDate: "", endDate: "" })
    setPaymentStatus("all")
    setPaymentMethod("all")
  }

  const handleChangePage = (nextPage) => {
    if (nextPage < 1) return
    if (totalPages > 0 && nextPage > totalPages) return
    setCurrentPage(nextPage)
  }

  const totalCount = tickets.data?.count || 0
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 1

  const openTicketModal = (id) => {
    setTicketId(id)
    setModalTicketOpen(true)
  }

  const closeTicketModal = () => {
    setModalTicketOpen(false)
  }

  return !permissions.some((e) => e.codename === "view_ticket") ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <React.Fragment>
      <div style={{ marginTop: "20px", marginBottom: "20px" }}></div>
      <div className="table-responsive">
        <Container fluid>
          <Alert color="danger" isOpen={tickets.isError}>
            {tickets.error?.errors?.map((error) => error?.message)}
          </Alert>
          <div>
            <Col md="12">
              <div className="ticket-by-user-filter-row">
                <div className="ticket-by-user-seller-search">
                  <Select
                    id="customer"
                    name="customer"
                    isClearable
                    values={""}
                    onChange={(value) => {
                      handleChangeSearch(value)
                      if (!value) {
                        setAppliedUser(null)
                        setCurrentPage(1)
                      }
                    }}
                    options={usersFiltered?.map((row) => ({
                      ...row,
                      label: `${row.first_name} ${row.last_name} - ${row.email}`,
                      value: row.id,
                    }))}
                    noOptionsMessage={() => "0 opciones"}
                    loadingMessage={() => "Cargando..."}
                    placeholder="Vendedor"
                  />
                </div>
                <div className="ticket-by-user-search-button">
                  <Button color="info" className="d-flex align-items-center" onClick={() => handleSearch()}>
                    <i className="me-2 fa fa-search" />
                    <span className="text-truncate">Buscar</span>
                  </Button>
                </div>
              </div>
              <div className="ticket-by-user-filters">
                <ListFilterBar
                  dateFilter={dateFilter}
                  onDateFilterChange={handleDateFilterChange}
                  selects={[
                    {
                      name: "paymentStatus",
                      label: "Estado de venta",
                      icon: "fa fa-receipt",
                      value: paymentStatus,
                      onChange: handlePaymentStatusChange,
                      options: [
                        { value: "all", label: "Todas" },
                        { value: "true", label: "Cobradas" },
                        { value: "false", label: "Pendientes" },
                        { value: "pagare", label: "Pagares" },
                      ],
                    },
                    {
                      name: "paymentMethod",
                      label: "Metodo de pago",
                      icon: "fa fa-credit-card",
                      value: paymentMethod,
                      onChange: handlePaymentMethodChange,
                      options: paymentMethodOptions,
                    },
                  ]}
                  onClear={clearListFilters}
                />
              </div>
            </Col>
            {!tickets.error && isLoading ? (
              <div className="my-5 text-center">
                <Loading isLoading={isLoading} size={2} />
              </div>
            ) : tickets.error && !tickets.isLoading ? (
              <div className="my-5 text-center">
                <h1>Error intenta mas tarde</h1>
              </div>
            ) : (
              <div className="table-responsive" style={{ marginTop: "30px" }}>
                <Table hover size="sm" className="mb-0 table-hover-effect table-interactive ticket-by-user-table">
                  <thead>
                    <tr>
                      <th style={styleCenterTable} className="text-cell">Folio</th>
                      <th style={styleCenterTable} className="text-cell">Fecha de creación</th>
                      <th style={styleCenterTable} className="text-cell">Cliente</th>
                      <th style={styleCenterTable} className="money-cell">Total</th>
                      <th style={styleCenterTable} className="payment-method-cell">Método de pago</th>
                      <th style={styleCenterTable} className="text-cell">Estado</th>
                      <th style={styleCenterTable} className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.data?.results?.length > 0 ? (
                      tickets.data?.results?.map((item) => {
                        return (
                          <TicketItemList
                            key={item.id}
                            item={item}
                            isOnlySeller={true}
                            openTicketModal={openTicketModal}
                          />
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center">
                          No hay datos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                {modalTicketOpen && (
                  <Modal
                    centered={true}
                    style={{ maxWidth: "500px", width: "95%" }}
                    backdrop={true}
                    isOpen={modalTicketOpen}
                    toggle={closeTicketModal}
                  >
                    <ModalHeader toggle={closeTicketModal}>Detalle de ticket</ModalHeader>
                    <ModalBody style={{ padding: "0px" }}>
                      <TicketRetrive id={ticketId} isModal={true} />
                    </ModalBody>
                  </Modal>
                )}
                <TablePaginationFooter
                  totalCount={totalCount}
                  currentPage={currentPage}
                  pageSize={PAGE_SIZE}
                  itemLabel="ventas"
                  isLoading={isLoading}
                  isFetching={tickets.isFetching}
                  onChangePage={handleChangePage}
                />
              </div>
            )}
          </div>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default TicketByUser
